import React from 'react';
import { CheckCircle, XCircle, MinusCircle } from '@phosphor-icons/react';

const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';
const GREEN = '#16624B';
const AMBER = '#A07A14';

/* Canonical AG document checklist — 6 required item types per the spec.
 * Kept as a module-level constant so the labels never drift across the
 * UI even if older records have a stale schema. */
export const CHECKLIST_TEMPLATE = [
  { id: 'formula',     label: 'Formula / ingredient list' },
  { id: 'sds_coa',     label: 'SDS / COA' },
  { id: 'packaging',   label: 'Packaging specs' },
  { id: 'claims',      label: 'Claims copy' },
  { id: 'use',         label: 'Product use instructions' },
  { id: 'test_data',   label: 'Supporting test data' },
];

const STATUS_META = {
  complete:       { label: 'Complete',       icon: CheckCircle, color: GREEN, bg: 'rgba(22,98,75,0.10)' },
  missing:        { label: 'Missing',        icon: XCircle,     color: '#B0312D', bg: 'rgba(176,49,45,0.08)' },
  not_applicable: { label: 'Not applicable', icon: MinusCircle, color: MUTED, bg: 'rgba(11,20,38,0.05)' },
};

/* Merge a record's persisted checklist with the canonical template so the
 * UI always shows the six expected rows, even when the brand has not yet
 * had a reviewer touch them. */
export function mergeChecklist(persisted) {
  const persistedById = Object.fromEntries((persisted || []).map(r => [r.id, r]));
  return CHECKLIST_TEMPLATE.map(t => ({
    id: t.id,
    label: t.label,
    status: persistedById[t.id]?.status || 'missing',
  }));
}

export default function DocumentChecklist({ value, onChange, disabled, testid = 'doc-checklist' }) {
  const rows = mergeChecklist(value);

  const setStatus = (id, status) => {
    if (disabled) return;
    const next = rows.map(r => r.id === id ? { ...r, status } : r);
    onChange?.(next);
  };

  return (
    <div data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: MUTED }}>
        Document checklist
      </p>
      <ul className="rounded-sm border divide-y" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}>
        {rows.map(r => {
          const meta = STATUS_META[r.status] || STATUS_META.missing;
          const Icon = meta.icon;
          return (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3 p-3"
              style={{ borderColor: RULE }}
              data-testid={`${testid}-row-${r.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Icon size={18} weight="fill" style={{ color: meta.color, flexShrink: 0 }} />
                <p className="text-sm truncate" style={{ color: SLATE }}>{r.label}</p>
              </div>
              <div className="flex items-center gap-1">
                {Object.entries(STATUS_META).map(([id, m]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setStatus(r.id, id)}
                    disabled={disabled}
                    className="px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.15em] border disabled:opacity-50"
                    style={{
                      borderColor: r.status === id ? m.color : RULE,
                      backgroundColor: r.status === id ? m.bg : 'transparent',
                      color: r.status === id ? m.color : MUTED,
                      fontWeight: r.status === id ? 700 : 500,
                    }}
                    data-testid={`${testid}-${r.id}-${id}`}
                    aria-pressed={r.status === id}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
